/**
 * Auto-layout voor het vereenvoudigde-kopie-diagram.
 *
 * Na het verbergen van concepten staan de resterende nodes nog op hun oude
 * plek → gaten. Deze helper herschikt de zichtbare **top-level** nodes met
 * dagre (hiërarchisch, top-down) zodat de kopie strak uitgelijnd is. Geneste
 * children (containerId) worden NIET los verplaatst — die houden hun relatieve
 * positie en rijden mee met hun container (die als één dagre-node meedoet).
 *
 * Fase 2: synthetische super-nodes (`type: "supernode"`) doen mee als
 * top-level layout-nodes. Relaties van/naar hun (verborgen) members worden
 * via `memberToSupernode` op de super-node geprojecteerd zodat dagre 'm op
 * een logische plek in de hiërarchie legt.
 *
 * Returnt per top-level entry-key een nieuwe absolute top-left-positie.
 */
import type { ViewState } from "@ae/domain";
import dagre from "@dagrejs/dagre";

interface LayoutRelation {
  sourceConceptId: string;
  targetConceptId: string;
}

export function layoutSimplifiedViewState(
  viewState: ViewState,
  hiddenConceptIds: Set<string>,
  relations: readonly LayoutRelation[],
  /** conceptId → supernode-entry-key voor samengevouwen members (Fase 2). */
  memberToSupernode?: Map<string, string>,
): Map<string, { x: number; y: number }> {
  const isVisibleNode = (key: string): boolean => {
    const ve = viewState[key];
    if (!ve?.position) return false;
    if (ve.type === "supernode") return true;
    if (ve.type !== "object") return false;
    return !hiddenConceptIds.has(ve.ref ?? key);
  };

  // Loop de containerId-keten omhoog naar de bovenste zichtbare container.
  const topAncestor = (key: string): string => {
    let cur = key;
    const seen = new Set<string>();
    while (!seen.has(cur)) {
      seen.add(cur);
      const parent = viewState[cur]?.containerId;
      if (!parent || !isVisibleNode(parent)) return cur;
      cur = parent;
    }
    return cur;
  };

  // Top-level zichtbare nodes = geen (zichtbare) container-parent.
  const topKeys: string[] = [];
  const conceptToKey = new Map<string, string>();
  for (const [key, ve] of Object.entries(viewState)) {
    if (!ve.position) continue;
    if (ve.type === "supernode") {
      topKeys.push(key);
      continue;
    }
    if (ve.type !== "object") continue;
    const cid = ve.ref ?? key;
    if (hiddenConceptIds.has(cid)) continue;
    if (!conceptToKey.has(cid)) conceptToKey.set(cid, key);
    if (topAncestor(key) === key) topKeys.push(key);
  }
  if (topKeys.length === 0) return new Map();

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 60, ranksep: 90, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));
  const dims = new Map<string, { w: number; h: number }>();
  for (const key of topKeys) {
    const ve = viewState[key];
    const w = ve?.width ?? 150;
    const h = ve?.height ?? 64;
    dims.set(key, { w, h });
    g.setNode(key, { width: w, height: h });
  }

  // Layout-anker voor een relatie-eindpunt: zichtbare concepten via hun
  // top-level container; samengevouwen members via hun super-node.
  const anchorFor = (conceptId: string): string | undefined => {
    if (!hiddenConceptIds.has(conceptId)) {
      const entry = conceptToKey.get(conceptId);
      return entry ? topAncestor(entry) : undefined;
    }
    return memberToSupernode?.get(conceptId);
  };

  const seenEdge = new Set<string>();
  for (const r of relations) {
    const s = anchorFor(r.sourceConceptId);
    const t = anchorFor(r.targetConceptId);
    if (!s || !t || s === t) continue;
    const ek = `${s} ${t}`;
    if (seenEdge.has(ek)) continue;
    seenEdge.add(ek);
    g.setEdge(s, t);
  }

  dagre.layout(g);

  const out = new Map<string, { x: number; y: number }>();
  for (const key of topKeys) {
    const p = g.node(key);
    const d = dims.get(key);
    if (!p || !d) continue;
    // dagre geeft het middelpunt; React-Flow wil de top-left.
    out.set(key, { x: p.x - d.w / 2, y: p.y - d.h / 2 });
  }
  return out;
}
